import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Star, MessageSquare, Trash2, AlertTriangle, Loader2 } from "lucide-react";
import { format, parseISO, formatDistanceToNow } from "date-fns";

const Stars = ({ rating }) => (
  <div className="flex items-center gap-0.5" data-testid={`stars-${rating}`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        className={`w-3.5 h-3.5 ${n <= rating ? "text-[#FFB000] fill-[#FFB000]" : "text-white/15"}`}
      />
    ))}
  </div>
);

export default function Feedback() {
  const [data, setData] = useState({ items: [], total: 0, average_rating: null });
  const [loading, setLoading] = useState(true);
  const [showClearModal, setShowClearModal] = useState(false);
  const [clearing, setClearing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/feedback");
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const handleClear = async () => {
    setClearing(true);
    try {
      const { data: resp } = await api.delete("/admin/feedback");
      toast.success(`Cleared ${resp.deleted} feedback record${resp.deleted === 1 ? "" : "s"}`);
      setShowClearModal(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clear feedback");
    } finally {
      setClearing(false);
    }
  };

  return (
    <div data-testid="feedback-page">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.15em] text-white/40 mb-2">
            Voice of Caller
          </div>
          <h1 className="text-3xl font-medium tracking-tight text-white">Caller Feedback</h1>
          <p className="text-sm text-white/50 mt-2 max-w-2xl">
            Submitted via the subtle popup on <span className="font-mono">repliantarc.com/ashley</span>{" "}
            during or after Ashley's call. Each rating links back to the originating consent
            submission for full context.
          </p>
        </div>
        {data.total > 0 && (
          <button
            type="button"
            onClick={() => setShowClearModal(true)}
            data-testid="feedback-clear-btn"
            className="flex items-center gap-2 px-3.5 py-2 rounded-md text-xs font-medium text-red-300 bg-red-500/5 hover:bg-red-500/10 border border-red-500/20 hover:border-red-500/40 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
            Clear all
          </button>
        )}
      </div>

      {showClearModal && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          data-testid="feedback-clear-modal"
          onClick={() => !clearing && setShowClearModal(false)}
        >
          <div
            className="bg-[#121214] border border-red-500/30 rounded-2xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-500/15 border border-red-500/30 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-400" strokeWidth={2} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Clear all feedback?</h2>
                <p className="text-sm text-white/55 mt-1.5 leading-relaxed">
                  This will permanently delete all <strong className="text-red-300">{data.total}</strong> feedback record{data.total === 1 ? "" : "s"}. The originating consent submissions and call records are preserved.
                </p>
                <p className="text-xs text-white/40 mt-3">This cannot be undone.</p>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={() => setShowClearModal(false)}
                disabled={clearing}
                className="flex-1 px-4 py-2.5 rounded-md text-sm font-medium text-white/70 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors disabled:opacity-50"
                data-testid="feedback-clear-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleClear}
                disabled={clearing}
                className="flex-1 px-4 py-2.5 rounded-md text-sm font-semibold text-white bg-red-500/80 hover:bg-red-500 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="feedback-clear-confirm"
              >
                {clearing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                {clearing ? "Clearing…" : "Yes, clear all"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-[#121214] border border-white/10 rounded-xl p-5">
          <div className="text-[11px] font-mono uppercase tracking-widest text-white/40">Responses</div>
          <div className="font-mono text-3xl text-white tracking-tighter mt-2" data-testid="feedback-total">
            {data.total}
          </div>
        </div>
        <div className="bg-[#121214] border border-white/10 rounded-xl p-5">
          <div className="text-[11px] font-mono uppercase tracking-widest text-white/40">Average Rating</div>
          <div className="font-mono text-3xl text-[#FFB000] tracking-tighter mt-2" data-testid="feedback-avg">
            {data.average_rating != null ? `${data.average_rating} / 5` : "—"}
          </div>
        </div>
        <div className="bg-[#121214] border border-white/10 rounded-xl p-5">
          <div className="text-[11px] font-mono uppercase tracking-widest text-white/40">Issues Reported</div>
          <div className="font-mono text-3xl text-red-400 tracking-tighter mt-2" data-testid="feedback-issues">
            {data.items.filter((i) => i.issues || i.call_connected === false || i.audio_clear === false).length}
          </div>
        </div>
      </div>

      <div className="bg-[#121214] border border-white/10 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-3">
          <MessageSquare className="w-4 h-4 text-[#FFB000]" />
          <div className="text-sm font-medium text-white">Recent feedback</div>
        </div>
        <div className="divide-y divide-white/5" data-testid="feedback-list">
          {loading && (
            <div className="px-5 py-10 text-center text-white/40 text-sm">Loading…</div>
          )}
          {!loading && data.items.length === 0 && (
            <div className="px-5 py-12 text-center text-white/40 text-sm">
              No feedback yet — once Ashley starts taking calls and the popup appears, responses land here.
            </div>
          )}
          {data.items.map((f) => (
            <div key={f.id} data-testid={`feedback-row-${f.id}`} className="px-5 py-4 hover:bg-white/[0.02]">
              <div className="flex items-start gap-4 flex-wrap">
                <div className="min-w-[160px]">
                  <Stars rating={f.rating} />
                  <div className="text-[11px] font-mono text-white/40 mt-1">
                    {f.created_at && formatDistanceToNow(parseISO(f.created_at), { addSuffix: true })}
                  </div>
                  {f.consent && (
                    <div className="text-[11px] font-mono text-white/60 mt-1">
                      {f.consent.name} · {f.consent.phone}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-[280px] space-y-2">
                  {f.comment && (
                    <div className="text-sm text-white/85 italic leading-relaxed">"{f.comment}"</div>
                  )}
                  <div className="flex flex-wrap gap-2 text-[11px]">
                    {f.call_connected === true && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400">
                        ✓ Connected
                      </span>
                    )}
                    {f.call_connected === false && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/30 text-red-400">
                        ✗ Did not connect
                      </span>
                    )}
                    {f.audio_clear === true && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400">
                        ✓ Audio clear
                      </span>
                    )}
                    {f.audio_clear === false && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300">
                        ⚠ Audio issues
                      </span>
                    )}
                  </div>
                  {f.issues && (
                    <div className="text-xs text-red-300 bg-red-500/5 border border-red-500/20 rounded p-2">
                      <span className="font-mono uppercase tracking-wider text-[10px]">Issue: </span>
                      {f.issues}
                    </div>
                  )}
                </div>
                <div className="text-[10px] font-mono text-white/30 max-w-[180px]">
                  IP: {f.ip}
                  <br />
                  {f.created_at && format(parseISO(f.created_at), "MMM d HH:mm")}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
